import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { bookApi, chapterApi } from '../services/api';

function Reader() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const chapterId = searchParams.get('chapter');

  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [fontSize, setFontSize] = useState(18);
  const [expandedVolumes, setExpandedVolumes] = useState({});

  const loadBookAndChapters = useCallback(async () => {
    try {
      setLoading(true);
      const [bookData, chaptersData] = await Promise.all([
        bookApi.getBook(id),
        bookApi.getBookChapters(id, { limit: 500 }),
      ]);
      setBook(bookData);
      setChapters(chaptersData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadChapterContent = useCallback(async (cid) => {
    try {
      setLoading(true);
      const [chapterData, contentData] = await Promise.all([
        chapterApi.getChapter(cid),
        chapterApi.getContent(cid),
      ]);
      setCurrentChapter(chapterData);
      setContent(contentData.content);
      navigate(`/books/${id}/read?chapter=${cid}`, { replace: true });
      
      // 在移动端浏览的时候，点击图书名称进入阅读页面，默认左侧的菜单应该向左收起
      if (window.innerWidth < 768) {
        setShowSidebar(false);
      }
      
      // 滚动内容区域到顶部
      const contentArea = document.getElementById('reader-content-area');
      if (contentArea) {
        contentArea.scrollTo(0, 0);
      } else {
        window.scrollTo(0, 0);
      }
      
      // 只有切换到不同卷时才折叠/展开
      // 找到当前点击章节所属的卷
      const foundVolume = Object.keys(chapterGroups).find(volume => 
        chapterGroups[volume].some(chapter => chapter.id === cid)
      );
      
      // 找到当前已显示章节所属的卷
      const currentVolume = currentChapter ? Object.keys(chapterGroups).find(volume => 
        chapterGroups[volume].some(chapter => chapter.id === currentChapter.id)
      ) : null;
      
      // 只有卷发生变化时才更新展开状态
      if (foundVolume && foundVolume !== currentVolume) {
        setExpandedVolumes(prev => {
          const newState = {};
          Object.keys(chapterGroups).forEach(volume => {
            newState[volume] = volume === foundVolume;
          });
          return newState;
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  useEffect(() => {
    loadBookAndChapters();
  }, [loadBookAndChapters]);

  useEffect(() => {
    if (chapterId) {
      loadChapterContent(chapterId);
    } else if (chapters.length > 0) {
      const firstCompleted = chapters.find(c => c.status === 'completed');
      if (firstCompleted) {
        loadChapterContent(firstCompleted.id);
      }
    }
  }, [chapterId, chapters, loadChapterContent]);

  const handlePrevChapter = () => {
    if (!currentChapter) return;
    const currentIndex = chapters.findIndex(c => c.id === currentChapter.id);
    if (currentIndex > 0) {
      const prevChapter = chapters[currentIndex - 1];
      if (prevChapter.status === 'completed') {
        loadChapterContent(prevChapter.id);
      }
    }
  };

  const handleNextChapter = () => {
    if (!currentChapter) return;
    const currentIndex = chapters.findIndex(c => c.id === currentChapter.id);
    if (currentIndex < chapters.length - 1) {
      const nextChapter = chapters[currentIndex + 1];
      if (nextChapter.status === 'completed') {
        loadChapterContent(nextChapter.id);
      }
    }
  };

  const getCurrentIndex = () => {
    if (!currentChapter) return -1;
    return chapters.findIndex(c => c.id === currentChapter.id);
  };

  // 按卷分组章节
  const groupChaptersByVolume = (chapters) => {
    const groups = {};
    chapters.forEach(chapter => {
      // 根据章节顺序分组：1-14为上册，15-31为下册
      let volume;
      if (chapter.chapter_order >= 1 && chapter.chapter_order <= 14) {
        volume = '上册';
      } else {
        volume = '下册';
      }
      
      if (!groups[volume]) {
        groups[volume] = [];
      }
      groups[volume].push(chapter);
    });
    return groups;
  };

  const toggleVolume = (volume) => {
    setExpandedVolumes(prev => ({
      ...prev,
      [volume]: !prev[volume]
    }));
  };

  const isVolumeExpanded = (volume) => {
    return expandedVolumes[volume] === true;
  };

  const chapterGroups = groupChaptersByVolume(chapters);
  const currentIndex = getCurrentIndex();
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < chapters.length - 1;



  if (loading && !currentChapter) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <p className="text-red-600 mb-4">加载失败: {error}</p>
          <button 
            onClick={() => navigate('/books')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回书籍列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-white flex overflow-hidden relative">
      {/* 移动端侧边栏遮罩 */}
      {showSidebar && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 md:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* 目录侧边栏 */}
      <div className={`
        fixed inset-y-0 left-0 z-40 md:relative 
        ${showSidebar ? 'translate-x-0 w-80' : '-translate-x-full md:translate-x-0 md:w-0 md:overflow-hidden'} 
        transition-all duration-300 bg-gray-50 border-r flex flex-col h-full
      `}>
        <div className={`p-4 border-b bg-white flex-shrink-0 ${!showSidebar ? 'hidden' : ''}`}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold text-gray-900">章节目录</h2>
            <button 
              onClick={() => setShowSidebar(false)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* 返回分类列表 */}
          {book?.category && (
            <Link 
              to={`/category/${book.category_id}`}
              className="flex items-center text-sm text-gray-500 hover:text-blue-600 transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>返回{book.category.name}</span>
            </Link>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {Object.entries(chapterGroups).map(([volume, volumeChapters]) => {
            const isExpanded = isVolumeExpanded(volume);
            const hasActiveChapter = volumeChapters.some(c => c.id === currentChapter?.id);
            
            return (
              <div key={volume} className="mb-4">
                <button
                  onClick={() => toggleVolume(volume)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-bold transition-colors ${
                    hasActiveChapter 
                      ? 'bg-blue-50 text-blue-700' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <span>{volume}</span>
                    <span className="ml-2 text-xs text-gray-400 font-normal">
                      ({volumeChapters.length}章)
                    </span>
                  </div>
                  <svg 
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {isExpanded && (
                  <div className="mt-2 ml-4 space-y-1 border-l-2 border-gray-200 pl-3">
                    {volumeChapters.map((chapter) => {
                      const isActive = currentChapter?.id === chapter.id;
                      return (
                        <button
                          key={chapter.id}
                          onClick={() => {
                            if (chapter.status === 'completed') {
                              loadChapterContent(chapter.id);
                            }
                          }}
                          disabled={chapter.status !== 'completed'}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                            isActive 
                              ? 'bg-blue-600 text-white shadow-md' 
                              : chapter.status === 'completed'
                                ? 'text-gray-700 hover:bg-gray-100 hover:pl-4'
                                : 'text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="truncate flex-1">
                              {chapter.title.replace(/三命通会·(.+?)·/, '')}
                            </span>
                            {isActive && (
                              <svg className="w-4 h-4 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 主内容区 */}
      <div id="reader-content-area" className="flex-1 flex flex-col min-w-0 h-full overflow-y-auto">
        {/* 顶部导航栏 */}
        <header className="sticky top-0 z-30 bg-white border-b shadow-sm flex-shrink-0">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {!showSidebar && (
                  <button 
                    onClick={() => setShowSidebar(true)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="显示目录"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                )}
                <button 
                  onClick={() => navigate('/books')}
                  className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span className="hidden sm:inline">返回</span>
                </button>
              </div>

              <h1 className="font-bold text-gray-900 truncate max-w-[200px] sm:max-w-md text-center">
                {currentChapter?.title || book?.title}
              </h1>

              <div className="flex items-center space-x-2">
                {/* 字体大小调节 */}
                <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                  <button 
                    onClick={() => setFontSize(s => Math.max(14, s - 2))}
                    className="px-2 py-1 text-sm hover:bg-white rounded transition-colors"
                  >
                    A-
                  </button>
                  <span className="text-xs text-gray-500 px-1">{fontSize}px</span>
                  <button 
                    onClick={() => setFontSize(s => Math.min(24, s + 2))}
                    className="px-2 py-1 text-lg hover:bg-white rounded transition-colors"
                  >
                    A+
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* 阅读内容 */}
        <main className="flex-1">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : currentChapter ? (
              <>
                {/* 章节标题 */}
                <div className="mb-8 text-center border-b pb-6">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    {currentChapter.title}
                  </h1>
                  <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                    <span>第 {currentIndex + 1} / {chapters.length} 章</span>
                    <span>•</span>
                    <span>{currentChapter.word_count?.toLocaleString()} 字</span>
                  </div>
                </div>

                {/* 正文内容 */}
                <div 
                  className="prose prose-lg max-w-none text-gray-800"
                  style={{ 
                    fontSize: `${fontSize}px`,
                    lineHeight: '1.8',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {content.split('\n\n').map((paragraph, index) => {
                    // 检测图片引用 [图片: /images/zsby/xxx.jpg]
                    const imageMatch = paragraph.match(/\[图片: (.+?)\]/);
                    if (imageMatch) {
                      return (
                        <div key={index} className="my-4 text-center">
                          <img 
                            src={imageMatch[1]} 
                            alt="配图" 
                            className="max-w-full h-auto mx-auto rounded-lg shadow-md"
                          />
                        </div>
                      );
                    }
                    
                    // 处理段落中的缩进（\t 转换为左边距）
                    const hasIndent = paragraph.startsWith('\t');
                    const indentLevel = (paragraph.match(/^\t+/g) || []).length;
                    const cleanParagraph = paragraph.replace(/^\t+/, '');
                    
                    // 如果有缩进，使用 padding-left 来模拟
                    const indentStyle = hasIndent ? { 
                      paddingLeft: `${indentLevel * 2}em`,
                      textIndent: indentLevel > 0 ? '0' : '2em'
                    } : {
                      textIndent: '2em'
                    };
                    
                    return (
                      <p 
                        key={index} 
                        className="mb-6 text-justify"
                        style={indentStyle}
                      >
                        {cleanParagraph || '\u00A0'}
                      </p>
                    );
                  })}
                </div>

                {/* 章节导航 */}
                <div className="mt-12 pt-8 border-t">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={handlePrevChapter}
                      disabled={!hasPrev}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                        hasPrev 
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                          : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      <span className="hidden sm:inline">上一章</span>
                    </button>

                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-1">阅读进度</p>
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${((currentIndex + 1) / chapters.length) * 100}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {Math.round(((currentIndex + 1) / chapters.length) * 100)}%
                      </p>
                    </div>

                    <button
                      onClick={handleNextChapter}
                      disabled={!hasNext}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                        hasNext 
                          ? 'bg-blue-600 text-white hover:bg-blue-700' 
                          : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      <span className="hidden sm:inline">下一章</span>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* 回到目录按钮 */}
                  <div className="mt-6 text-center">
                    <button
                      onClick={() => setShowSidebar(true)}
                      className="text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center space-x-1 mx-auto transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      <span>打开目录</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p className="text-gray-500 mb-4">请选择要阅读的章节</p>
                <button 
                  onClick={() => setShowSidebar(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  打开目录
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Reader;